import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Row, Col, Button, ProgressBar, Spinner, Tabs, Tab } from 'react-bootstrap';
import { WindowPanel } from '../../WindowPanel';
import { SnapBarError } from '../../ConfirmationDialog';
import ApiDataService from '../../services/ApiDataService';
import FileManager from '../FileManager';

const BATCH_SIZE = 20;
const POLL_DELAY_MS = 400;

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return '0 MB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

const BACKUP_PER_PAGE = 50;

const ImageOptimize = () => {
  const [scanning, setScanning] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [bytesSaved, setBytesSaved] = useState(0);
  const [errors, setErrors] = useState([]);
  const [snap, setSnap] = useState({ error: '', snapopen: false, snapcolor: '' });
  const [activeTab, setActiveTab] = useState('optimize');
  const stopRef = useRef(false);

  const [backupLoading, setBackupLoading] = useState(false);
  const [backupData, setBackupData] = useState(null);
  const [backupPage, setBackupPage] = useState(1);
  const [backupStatus, setBackupStatus] = useState('all');
  const [backupSearch, setBackupSearch] = useState('');
  const [backupSearchInput, setBackupSearchInput] = useState('');

  const [usageScanning, setUsageScanning] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [usagePage, setUsagePage] = useState(1);
  const [usageFilter, setUsageFilter] = useState('all');
  const [usageSearch, setUsageSearch] = useState('');
  const [usageSearchInput, setUsageSearchInput] = useState('');
  const [usageHasScanned, setUsageHasScanned] = useState(false);

  const snapclose = () => setSnap({ error: '', snapopen: false, snapcolor: '' });

  useEffect(() => {
    return () => { stopRef.current = true; };
  }, []);

  const runBatch = useCallback(() => {
    if (stopRef.current) return;
    const formData = new FormData();
    formData.append('batch_size', BATCH_SIZE);

    ApiDataService.post('admin/portal/image-optimize/process-batch', formData).then(response => {
      if (response.data.return_status !== "0") {
        setRunning(false);
        setSnap({ error: response.data.error_message || 'Batch failed', snapopen: true, snapcolor: '#ff4c4ceb' });
        return;
      }
      const result = response.data.result;
      setProgress({
        total: result.total,
        done: result.done,
        errors_count: result.errors_count,
        pending: result.pending,
      });
      setBytesSaved(prev => prev + (result.bytes_saved_this_batch || 0));
      if (result.errors && result.errors.length) {
        setErrors(result.errors);
      }
      if (!result.is_complete && !stopRef.current) {
        setTimeout(runBatch, POLL_DELAY_MS);
      } else {
        setRunning(false);
      }
    }).catch(error => {
      setRunning(false);
      setSnap({ error: error.message, snapopen: true, snapcolor: '#ff4c4ceb' });
    });
  }, []);

  const handleStart = () => {
    setScanning(true);
    setErrors([]);
    setBytesSaved(0);
    setProgress(null);
    ApiDataService.post('admin/portal/image-optimize/scan', new FormData()).then(response => {
      setScanning(false);
      if (response.data.return_status !== "0") {
        setSnap({ error: response.data.error_message || 'Scan failed', snapopen: true, snapcolor: '#ff4c4ceb' });
        return;
      }
      const result = response.data.result;
      setProgress({
        total: result.total,
        done: result.already_done,
        errors_count: 0,
        pending: result.pending,
      });
      if (result.pending > 0) {
        stopRef.current = false;
        setRunning(true);
        runBatch();
      }
    }).catch(error => {
      setScanning(false);
      setSnap({ error: error.message, snapopen: true, snapcolor: '#ff4c4ceb' });
    });
  };

  const handleStop = () => {
    stopRef.current = true;
    setRunning(false);
  };

  const loadBackupList = useCallback((page, status, search) => {
    setBackupLoading(true);
    const formData = new FormData();
    formData.append('page', page);
    formData.append('per_page', BACKUP_PER_PAGE);
    formData.append('status', status);
    formData.append('search', search);

    ApiDataService.post('admin/portal/image-optimize/backup-list', formData).then(response => {
      setBackupLoading(false);
      if (response.data.return_status !== "0") {
        setSnap({ error: response.data.error_message || 'Could not load backup list', snapopen: true, snapcolor: '#ff4c4ceb' });
        return;
      }
      setBackupData(response.data.result);
    }).catch(error => {
      setBackupLoading(false);
      setSnap({ error: error.message, snapopen: true, snapcolor: '#ff4c4ceb' });
    });
  }, []);

  useEffect(() => {
    if (activeTab === 'backup') {
      loadBackupList(backupPage, backupStatus, backupSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, backupPage, backupStatus, backupSearch]);

  const handleBackupSearchSubmit = (e) => {
    e.preventDefault();
    setBackupPage(1);
    setBackupSearch(backupSearchInput.trim());
  };

  const loadUsageList = useCallback((page, filter, search) => {
    setUsageLoading(true);
    const formData = new FormData();
    formData.append('page', page);
    formData.append('per_page', BACKUP_PER_PAGE);
    formData.append('filter', filter);
    formData.append('search', search);

    ApiDataService.post('admin/portal/image-optimize/usage-list', formData).then(response => {
      setUsageLoading(false);
      if (response.data.return_status !== "0") {
        setUsageData(null);
        // "No scan has been run yet" is expected on first visit to this tab -
        // not a real error, just prompts the Scan button below instead of a
        // red snackbar. Anything else (a real DB/file error) still surfaces.
        const message = response.data.error_message || '';
        if (!message.includes('No usage scan has been run yet')) {
          setSnap({ error: message || 'Could not load usage list', snapopen: true, snapcolor: '#ff4c4ceb' });
        }
        return;
      }
      setUsageData(response.data.result);
      setUsageHasScanned(true);
    }).catch(error => {
      setUsageLoading(false);
      setSnap({ error: error.message, snapopen: true, snapcolor: '#ff4c4ceb' });
    });
  }, []);

  useEffect(() => {
    if (activeTab === 'usage') {
      loadUsageList(usagePage, usageFilter, usageSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, usagePage, usageFilter, usageSearch]);

  const handleUsageScan = () => {
    setUsageScanning(true);
    ApiDataService.post('admin/portal/image-optimize/usage-scan', new FormData()).then(response => {
      setUsageScanning(false);
      if (response.data.return_status !== "0") {
        setSnap({ error: response.data.error_message || 'Scan failed', snapopen: true, snapcolor: '#ff4c4ceb' });
        return;
      }
      setUsagePage(1);
      loadUsageList(1, usageFilter, usageSearch);
    }).catch(error => {
      setUsageScanning(false);
      setSnap({ error: error.message, snapopen: true, snapcolor: '#ff4c4ceb' });
    });
  };

  const handleUsageSearchSubmit = (e) => {
    e.preventDefault();
    setUsagePage(1);
    setUsageSearch(usageSearchInput.trim());
  };

  const percent = progress && progress.total > 0
    ? Math.round(((progress.done + progress.errors_count) / progress.total) * 100)
    : 0;

  return (
    <>
      <SnapBarError
        message={snap.error}
        snapopen={snap.snapopen}
        snapcolor={snap.snapcolor}
        snapclose={snapclose}
      />

      <WindowPanel rawHtml={
        <div className="windowContent">
          <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key)} className="mb-3">
            <Tab eventKey="optimize" title="Bulk Optimize">
              <Row>
                <Col sm="12">
                  <p>
                    Recompresses every existing image under <code>uploads/</code> (any nesting depth) in place.
                    A copy of each original is kept before it's touched, and any file that fails re-verification
                    after compression is restored automatically.
                  </p>
                </Col>
                <Col sm="12">
                  <Button onClick={handleStart} disabled={scanning || running}>
                    {scanning ? <Spinner animation="border" size="sm" /> : 'Start'}
                  </Button>
                  {running && (
                    <Button variant="secondary" className="ml-2" onClick={handleStop}>
                      Stop
                    </Button>
                  )}
                </Col>
              </Row>

              {progress && (
                <Row className="my-4">
                  <Col sm="12">
                    <ProgressBar now={percent} label={`${percent}%`} />
                    <p className="mt-2">
                      {progress.done} done, {progress.errors_count} errors, {progress.pending} pending
                      &nbsp;of {progress.total} total &mdash; {formatBytes(bytesSaved)} saved this run
                    </p>
                  </Col>
                </Row>
              )}

              {errors.length > 0 && (
                <Row className="my-4">
                  <Col sm="12">
                    <h6>Errors ({errors.length} shown, most recent)</h6>
                    <table className="table table-sm">
                      <thead>
                        <tr><th>File</th><th>Error</th></tr>
                      </thead>
                      <tbody>
                        {errors.map((e, i) => (
                          <tr key={i}><td>{e.path}</td><td>{e.error}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </Col>
                </Row>
              )}
            </Tab>
            <Tab eventKey="backup" title="Backup">
              {activeTab === 'backup' && (
                <>
                  <Row>
                    <Col sm="12">
                      <p>
                        Every file the Bulk Optimize job (or File Manager&apos;s per-file Optimize) has ever touched
                        keeps an untouched copy here before compression, so a bad run can always be restored manually.
                        This view is read-only.
                      </p>
                    </Col>
                  </Row>

                  {backupData && !backupData.error && (
                    <Row className="my-3">
                      <Col sm="12">
                        <p className="mb-1">
                          <strong>{backupData.total_matching}</strong> backed-up files matching the filter below
                          &nbsp;&mdash;&nbsp; <strong>{formatBytes(backupData.total_backup_bytes)}</strong> held in backups
                          &nbsp;&mdash;&nbsp; <strong>{formatBytes(backupData.total_saved_bytes)}</strong> saved by compression so far
                          {backupData.missing_backups > 0 && (
                            <span className="text-danger">
                              &nbsp;&mdash;&nbsp; {backupData.missing_backups} backup file(s) missing from disk
                            </span>
                          )}
                        </p>
                        <p className="text-muted mb-0"><small>Backup folder: {backupData.backup_dir}</small></p>
                      </Col>
                    </Row>
                  )}

                  <Row className="my-3">
                    <Col sm="6" md="4">
                      <select
                        className="form-control"
                        value={backupStatus}
                        onChange={(e) => { setBackupPage(1); setBackupStatus(e.target.value); }}
                      >
                        <option value="all">All statuses</option>
                        <option value="done">Done</option>
                        <option value="error">Error (rolled back)</option>
                      </select>
                    </Col>
                    <Col sm="6" md="5">
                      <form onSubmit={handleBackupSearchSubmit}>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by path..."
                          value={backupSearchInput}
                          onChange={(e) => setBackupSearchInput(e.target.value)}
                        />
                      </form>
                    </Col>
                    <Col sm="12" md="3">
                      <Button type="button" onClick={handleBackupSearchSubmit} disabled={backupLoading}>
                        {backupLoading ? <Spinner animation="border" size="sm" /> : 'Search'}
                      </Button>
                    </Col>
                  </Row>

                  {backupData && backupData.error && (
                    <Row className="my-3"><Col sm="12"><p className="text-danger">{backupData.error}</p></Col></Row>
                  )}

                  {backupData && !backupData.error && (
                    <Row className="my-3">
                      <Col sm="12">
                        <div style={{ overflowX: 'auto' }}>
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Path</th>
                                <th>Status</th>
                                <th>Original</th>
                                <th>Compressed</th>
                                <th>Saved</th>
                                <th>Backup on disk</th>
                              </tr>
                            </thead>
                            <tbody>
                              {backupData.rows.map((row, i) => (
                                <tr key={i}>
                                  <td style={{ wordBreak: 'break-all' }}>{row.path}</td>
                                  <td>
                                    {row.status === 'error'
                                      ? <span className="text-danger" title={row.error || ''}>error</span>
                                      : 'done'}
                                  </td>
                                  <td>{formatBytes(row.original_size)}</td>
                                  <td>{row.status === 'done' ? formatBytes(row.new_size) : '-'}</td>
                                  <td>{row.bytes_saved !== null ? formatBytes(row.bytes_saved) : '-'}</td>
                                  <td>
                                    {row.backup_exists
                                      ? formatBytes(row.backup_size)
                                      : <span className="text-danger">missing</span>}
                                  </td>
                                </tr>
                              ))}
                              {backupData.rows.length === 0 && (
                                <tr><td colSpan="6" className="text-center text-muted">No backed-up files match this filter</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {backupData.total_pages > 1 && (
                          <div className="d-flex align-items-center justify-content-between">
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={backupPage <= 1 || backupLoading}
                              onClick={() => setBackupPage(p => Math.max(1, p - 1))}
                            >
                              Previous
                            </Button>
                            <span>Page {backupData.page} of {backupData.total_pages}</span>
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={backupPage >= backupData.total_pages || backupLoading}
                              onClick={() => setBackupPage(p => p + 1)}
                            >
                              Next
                            </Button>
                          </div>
                        )}
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </Tab>
            <Tab eventKey="usage" title="DB Usage">
              {activeTab === 'usage' && (
                <>
                  <Row>
                    <Col sm="12">
                      <div className="alert alert-warning">
                        <strong>Read this before trusting a "Not confirmed" result:</strong> this only checks
                        structured image-path columns (category, header, footer, homepage, product, SEO, etc).
                        It does <strong>not</strong> scan rich-text content (blog post body, category "more info",
                        and similar CKEditor fields) for images pasted directly into the content. A file marked
                        "Not confirmed" is <strong>not proven unused</strong> &mdash; it only means none of the
                        checked columns reference it. Do not delete a file based on this alone.
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col sm="12">
                      <Button onClick={handleUsageScan} disabled={usageScanning}>
                        {usageScanning ? <Spinner animation="border" size="sm" /> : (usageHasScanned ? 'Re-scan' : 'Scan')}
                      </Button>
                      {usageData && (
                        <span className="ml-3 text-muted">
                          <small>Last scanned: {new Date(usageData.generated_at).toLocaleString()}</small>
                        </span>
                      )}
                    </Col>
                  </Row>

                  {usageData && (
                    <Row className="my-3">
                      <Col sm="12">
                        <p className="mb-1">
                          <strong>{usageData.total_files}</strong> image files scanned across all sites
                          &nbsp;&mdash;&nbsp; <strong>{usageData.confirmed_used_count}</strong> confirmed used in DB
                          &nbsp;&mdash;&nbsp; <strong>{usageData.not_confirmed_count}</strong> not found in checked columns
                        </p>
                        {usageData.skipped_sources && usageData.skipped_sources.length > 0 && (
                          <p className="text-danger mb-0">
                            <small>
                              {usageData.skipped_sources.length} source(s) could not be queried and were skipped
                              (see console) - results are based on the remaining sources only.
                            </small>
                          </p>
                        )}
                      </Col>
                    </Row>
                  )}

                  {!usageData && !usageLoading && (
                    <Row className="my-3">
                      <Col sm="12"><p className="text-muted">No scan yet - click Scan above to check every uploaded image against the DB.</p></Col>
                    </Row>
                  )}

                  {usageData && (
                    <>
                      <Row className="my-3">
                        <Col sm="6" md="4">
                          <select
                            className="form-control"
                            value={usageFilter}
                            onChange={(e) => { setUsagePage(1); setUsageFilter(e.target.value); }}
                          >
                            <option value="all">All files</option>
                            <option value="confirmed_used">Confirmed used</option>
                            <option value="not_confirmed">Not confirmed</option>
                          </select>
                        </Col>
                        <Col sm="6" md="5">
                          <form onSubmit={handleUsageSearchSubmit}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search by path..."
                              value={usageSearchInput}
                              onChange={(e) => setUsageSearchInput(e.target.value)}
                            />
                          </form>
                        </Col>
                        <Col sm="12" md="3">
                          <Button type="button" onClick={handleUsageSearchSubmit} disabled={usageLoading}>
                            {usageLoading ? <Spinner animation="border" size="sm" /> : 'Search'}
                          </Button>
                        </Col>
                      </Row>

                      <Row className="my-3">
                        <Col sm="12">
                          <div style={{ overflowX: 'auto' }}>
                            <table className="table table-sm">
                              <thead>
                                <tr>
                                  <th>Path</th>
                                  <th>Size</th>
                                  <th>DB status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {usageData.rows.map((row, i) => (
                                  <tr key={i}>
                                    <td style={{ wordBreak: 'break-all' }}>{row.path}</td>
                                    <td>{formatBytes(row.size)}</td>
                                    <td>
                                      {row.confirmed_used
                                        ? <span className="text-success">confirmed used</span>
                                        : <span className="text-warning">not confirmed</span>}
                                    </td>
                                  </tr>
                                ))}
                                {usageData.rows.length === 0 && (
                                  <tr><td colSpan="3" className="text-center text-muted">No files match this filter</td></tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          {usageData.total_pages > 1 && (
                            <div className="d-flex align-items-center justify-content-between">
                              <Button
                                variant="secondary"
                                size="sm"
                                disabled={usagePage <= 1 || usageLoading}
                                onClick={() => setUsagePage(p => Math.max(1, p - 1))}
                              >
                                Previous
                              </Button>
                              <span>Page {usageData.page} of {usageData.total_pages}</span>
                              <Button
                                variant="secondary"
                                size="sm"
                                disabled={usagePage >= usageData.total_pages || usageLoading}
                                onClick={() => setUsagePage(p => p + 1)}
                              >
                                Next
                              </Button>
                            </div>
                          )}
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              )}
            </Tab>
            <Tab eventKey="files" title="File Manager">
              {activeTab === 'files' && <FileManager />}
            </Tab>
          </Tabs>
        </div>
      } />
    </>
  );
};

export default ImageOptimize;
